import { forwardRef } from "react";
import Link from "next/link";

import { Anchor, Button } from "@newsela/angelou";
import { actionNames, objectTypes, withTrackEvent } from "@newsela/snitch";
import BaseCard from "@/components/BaseCard";
import AdminContentSettingsCardFooter from "@/admin/AdminContentSettingsCardFooter";

import { useContentCard } from "../hooks/useContentCard";
import { SMART_BUNDLES_LABEL } from "../ContentCardV2.constants";

import {
  BundleCardMetadata,
  CardViewmodelAndProps,
} from "../ContentCardV2.interface";

import {
  cardActionsContainerCss,
  contentSettingsModeCss,
  linkCss,
} from "./styles";

const AnchorWithTrackEvent = withTrackEvent(Anchor);

export const BundleCard = forwardRef<HTMLElement, CardViewmodelAndProps>(
  (props, ref) => {
    const {
      id: contentId,
      contentSettingsGroups,
      dataQaSelector = "",
      display_date: displayDate,
      images = [],
      isContentSettingsEnabled,
      metadata,
      title,
      url,
      visibility_markers: visibilityMarkers,
    } = props;

    const {
      content_type: contentType,
      label = "",
      asset_count: assetCount,
    } = metadata as BundleCardMetadata;

    const { assignHref, badges, showAssignmentAnchor } = useContentCard({
      contentId,
      contentType,
      label,
      visibilityMarkers,
    });

    return (
      <BaseCard
        ref={ref}
        badges={badges}
        contentId={contentId}
        // The database shows that only Smart Bundles have no label (but we have
        // problematic Bundles with no "data"), and the Product team decided to
        // use a different designation for such cards.
        // NOTE: we could be more defensive and also check if the bundle has no
        // `assetCount`; Smart Bundles also don't have counts.
        contentTag={label || SMART_BUNDLES_LABEL}
        dataQaSelector={dataQaSelector}
        displayDate={displayDate}
        images={images}
        title={title}
        url={url}
        assetCount={assetCount}
        specialContent={
          <>
            {isContentSettingsEnabled && (
              <AdminContentSettingsCardFooter
                contentId={contentId}
                contentSettingsGroups={contentSettingsGroups || []}
                visibilityMarkers={visibilityMarkers}
                style={contentSettingsModeCss}
              />
            )}

            <div css={cardActionsContainerCss}>
              {showAssignmentAnchor && (
                <AnchorWithTrackEvent
                  __cssFor={linkCss}
                  styleAnchorAsButton={{
                    flavor: Button.flavors.muted,
                    size: Button.sizes.lg,
                  }}
                  href={assignHref}
                  actionName={actionNames.navigate}
                  objectType={objectTypes.content}
                  streamProperties={{
                    content_type: contentType,
                    description:
                      "Navigate to create new assignment from bundle content card",
                  }}
                  __renderCustomLink={({ children, href, ...anchorProps }) => {
                    return (
                      <Link
                        href={typeof href === "string" ? href : ""}
                        data-qa-selector="bundle-card-assign-link"
                        {...anchorProps}
                        aria-label="Create a new assignment from this content"
                      >
                        {children}
                      </Link>
                    );
                  }}
                >
                  Assign
                </AnchorWithTrackEvent>
              )}
            </div>
          </>
        }
      />
    );
  },
);
